// SIMPLIFIED FORMATTING - NO CONVERSION, DIRECT DISPLAY
export const formatSessionAmount = (amount) => {
  if (!amount && amount !== 0) return '₹0.00';
  
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) return '₹0.00';
  
  return `₹${numAmount.toLocaleString('en-IN', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  })}`;
};

// Session Project Constants
export const SESSION_PROJECT_VALIDATION_LIMITS = {
  PROJECT_NAME_MIN_LENGTH: 3,
  PROJECT_NAME_MAX_LENGTH: 200,
  AMOUNT_MIN: 0,
  AMOUNT_MAX: 99999,
  SOURCE_MIN_LENGTH: 1,
  SOURCE_MAX_LENGTH: 100,
};

// Clearance type options
export const SESSION_CLEARANCE_TYPE_OPTIONS = [
  { value: 'state_plan', label: 'State Plan' },
  { value: 'negotiated_loan', label: 'Negotiated Loan' }
];

// Project status options
export const SESSION_PROJECT_STATUS_OPTIONS = [
  { value: 'new_project', label: 'New Project' },
  { value: 'ongoing_project', label: 'Ongoing Project' }
];

// Default session project structure with dual lock fields
export const DEFAULT_SESSION_PROJECT = {
  department: '',
  projectName: '',
  amount: 0,
  clearanceType: 'state_plan',
  source: '',
  projectStatus: 'new_project',
  financialYear: '',
  
  // Project data locks (SessionBudgetDescriptive)
  isProjectLocked: false,
  projectLockedAt: null,
  projectLockedBy: null,
  projectLockedByName: null,
  
  // Clearance data locks (ClearanceProjectWise)
  isClearanceLocked: false,
  clearanceLockedAt: null,
  clearanceLockedBy: null,
  clearanceLockedByName: null,
  
  // Standard fields
  createdAt: null,
  updatedAt: null,
  createdBy: null
};

// Calculate department totals
export const calculateDepartmentTotals = (projects) => {
  const departmentTotals = {};
  
  projects.forEach(project => {
    if (project.status === 'approved' && project.department) {
      if (!departmentTotals[project.department]) {
        departmentTotals[project.department] = {
          department: project.department,
          sector: project.sector,
          approvedTotal: 0,
          approvedStatePlan: 0,
          approvedNegotiatedLoan: 0,
          projectCount: 0
        };
      }
      
      const approved = parseFloat(project.financialRequirement) || 0;
      departmentTotals[project.department].approvedTotal += approved;
      departmentTotals[project.department].projectCount += 1;
    }
  });
  
  return Object.values(departmentTotals);
};

// Calculate session project totals
export const calculateSessionProjectTotals = (sessionProjects) => {
  const departmentTotals = {};
  
  sessionProjects.forEach(project => {
    if (!project.department) return;
    
    if (!departmentTotals[project.department]) {
      departmentTotals[project.department] = {
        department: project.department,
        totalAmount: 0,
        statePlanAmount: 0,
        negotiatedLoanAmount: 0,
        projectCount: 0
      };
    }
    
    const dept = departmentTotals[project.department];
    const amount = parseFloat(project.amount) || 0;
    
    dept.totalAmount += amount;
    dept.projectCount += 1;
    
    if (project.clearanceType === 'state_plan') {
      dept.statePlanAmount += amount;
    } else if (project.clearanceType === 'negotiated_loan') {
      dept.negotiatedLoanAmount += amount;
    }
  });
  
  return Object.values(departmentTotals);
};

// Calculate clearance totals
export const calculateClearanceTotals = (clearanceData, projects) => {
  const departmentTotals = {};
  
  Object.entries(clearanceData).forEach(([projectId, clearance]) => {
    const project = projects.find(p => p.id === projectId);
    if (!project || project.status !== 'approved') return;
    
    const department = project.department;
    if (!departmentTotals[department]) {
      departmentTotals[department] = {
        department: department,
        sector: project.sector,
        clearanceTotal: 0,
        clearanceStatePlan: 0,
        clearanceNegotiatedLoan: 0,
        projectCount: 0
      };
    }
    
    const allocatedAmount = parseFloat(clearance.allocatedAmount) || 0;
    const clearanceType = clearance.clearanceType;
    
    departmentTotals[department].clearanceTotal += allocatedAmount;
    
    if (clearanceType === 'state_plan') {
      departmentTotals[department].clearanceStatePlan += allocatedAmount;
    } else if (clearanceType === 'negotiated_loan') {
      departmentTotals[department].clearanceNegotiatedLoan += allocatedAmount;
    }
    
    departmentTotals[department].projectCount += 1;
  });
  
  return departmentTotals;
};

// Calculate approved amounts by clearance type
export const calculateApprovedByClearanceType = (projects, clearanceData) => {
  const departmentTotals = {};
  
  projects.forEach(project => {
    if (project.status !== 'approved' || !project.department) return;
    
    const department = project.department;
    if (!departmentTotals[department]) {
      departmentTotals[department] = {
        department: department,
        sector: project.sector,
        approvedTotal: 0,
        approvedStatePlan: 0,
        approvedNegotiatedLoan: 0
      };
    }
    
    const approvedAmount = parseFloat(project.financialRequirement) || 0;
    const clearance = clearanceData[project.id];
    const clearanceType = clearance?.clearanceType;
    
    departmentTotals[department].approvedTotal += approvedAmount;
    
    if (clearanceType === 'state_plan') {
      departmentTotals[department].approvedStatePlan += approvedAmount;
    } else if (clearanceType === 'negotiated_loan') {
      departmentTotals[department].approvedNegotiatedLoan += approvedAmount;
    }
  });
  
  return Object.values(departmentTotals);
};

// Combine approved and clearance data
export const combineSessionData = (projects, clearanceData) => {
  const approvedTotals = calculateApprovedByClearanceType(projects, clearanceData);
  const clearanceTotals = calculateClearanceTotals(clearanceData, projects);
  
  const combinedData = {};
  
  approvedTotals.forEach(dept => {
    combinedData[dept.department] = {
      department: dept.department,
      sector: dept.sector,
      approvedTotal: dept.approvedTotal,
      approvedStatePlan: dept.approvedStatePlan,
      approvedNegotiatedLoan: dept.approvedNegotiatedLoan,
      clearanceTotal: 0,
      clearanceStatePlan: 0,
      clearanceNegotiatedLoan: 0
    };
  });
  
  Object.values(clearanceTotals).forEach(clearanceDept => {
    if (combinedData[clearanceDept.department]) {
      combinedData[clearanceDept.department].clearanceTotal = clearanceDept.clearanceTotal;
      combinedData[clearanceDept.department].clearanceStatePlan = clearanceDept.clearanceStatePlan;
      combinedData[clearanceDept.department].clearanceNegotiatedLoan = clearanceDept.clearanceNegotiatedLoan;
    } else {
      combinedData[clearanceDept.department] = {
        department: clearanceDept.department,
        sector: clearanceDept.sector,
        approvedTotal: 0,
        approvedStatePlan: 0,
        approvedNegotiatedLoan: 0,
        clearanceTotal: clearanceDept.clearanceTotal,
        clearanceStatePlan: clearanceDept.clearanceStatePlan,
        clearanceNegotiatedLoan: clearanceDept.clearanceNegotiatedLoan
      };
    }
  });
  
  return Object.values(combinedData);
};

// Calculate sector totals
export const calculateSectorTotals = (departmentData) => {
  const sectorTotals = {};
  
  departmentData.forEach(dept => {
    if (!sectorTotals[dept.sector]) {
      sectorTotals[dept.sector] = {
        sector: dept.sector,
        approvedTotal: 0,
        approvedStatePlan: 0,
        approvedNegotiatedLoan: 0,
        clearanceTotal: 0,
        clearanceStatePlan: 0,
        clearanceNegotiatedLoan: 0,
        departmentCount: 0
      };
    }
    
    sectorTotals[dept.sector].approvedTotal += dept.approvedTotal || 0;
    sectorTotals[dept.sector].approvedStatePlan += dept.approvedStatePlan || 0;
    sectorTotals[dept.sector].approvedNegotiatedLoan += dept.approvedNegotiatedLoan || 0;
    sectorTotals[dept.sector].clearanceTotal += dept.clearanceTotal || 0;
    sectorTotals[dept.sector].clearanceStatePlan += dept.clearanceStatePlan || 0;
    sectorTotals[dept.sector].clearanceNegotiatedLoan += dept.clearanceNegotiatedLoan || 0;
    sectorTotals[dept.sector].departmentCount += 1;
  });
  
  return sectorTotals;
};